import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Card, Tag, Button, Dropdown, Menu, Spin, Empty, Popconfirm } from "antd";
import {
  PlayCircleOutlined, UserOutlined, WarningOutlined, DownOutlined,
  EnvironmentOutlined, DashboardOutlined, ToolOutlined, SafetyOutlined,
  QrcodeOutlined, CameraOutlined,
  CheckCircleFilled, CloseCircleFilled, ClockCircleOutlined,
  PaperClipOutlined, DownloadOutlined,
} from "@ant-design/icons";
import { Map as LeafletMap, TileLayer, CircleMarker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import moment from "moment";
import { assetStates } from "@iso/constants/assetStates";
import { apiEndpoint } from "@iso/lib/helpers/endpoint";
import FileUploader from "@iso/components/FileUploader/FileUploader";

// Esri World Imagery — free satellite tiles, no API key needed (the app's Google
// key is a placeholder locally). Gives a real satellite view of the asset's spot.
const SAT_TILES = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";

const BRAND = "#0B2A1B";
const GREEN = "rgb(85, 159, 47)";
const AMBER = "#f5a01b";
const RED = "#e24e4c";
const CARD_SHADOW = "0 1px 3px rgba(16,24,40,0.06), 0 1px 2px rgba(16,24,40,0.05)";
const cardStyle = { borderRadius: 12, height: "100%", boxShadow: CARD_SHADOW };

const dash = (v) => (v === null || v === undefined || v === "" ? "—" : v);
const fmtDate = (d) => (d ? moment(d).format("ll") : "—");
const fromNow = (d) => (d ? moment(d).fromNow() : "—");

const stateColor = (raw) => {
  const m = assetStates.find((s) => s.value === raw);
  return m ? m.color : "rgb(120,120,120)";
};
const stateLabel = (raw) => {
  const m = assetStates.find((s) => s.value === raw);
  return m ? m.label : raw ? raw.replace(/_/g, " ") : "Unknown";
};

const serviceDue = (o) => {
  if (o.next_service_due_date) {
    const days = moment(o.next_service_due_date).startOf("day").diff(moment().startOf("day"), "days");
    return {
      main: days < 0 ? `${Math.abs(days)} days overdue` : days === 0 ? "Due today" : `In ${days} days`,
      sub: fmtDate(o.next_service_due_date),
      overdue: days < 0,
    };
  }
  if (o.next_service_due_reading) {
    const cur = Number(o.current_reading) || 0;
    const left = Number(o.next_service_due_reading) - cur;
    const unit = o.reading_unit || "";
    return {
      main: left < 0 ? `${Math.abs(left)} ${unit} overdue` : `In ${left} ${unit}`.trim(),
      sub: `at ${o.next_service_due_reading} ${unit}`.trim(),
      overdue: left < 0,
    };
  }
  return { main: "Not scheduled", sub: "", overdue: false };
};

const warrantyStatus = (d) => {
  if (!d) return { label: "—", color: "default" };
  const days = moment(d).startOf("day").diff(moment().startOf("day"), "days");
  if (days < 0) return { label: "Expired", color: "red" };
  if (days <= 30) return { label: `Expires in ${days}d`, color: "orange" };
  return { label: "Current", color: "green" };
};

// A rounded, tinted icon chip used throughout for visual richness.
const Chip = ({ children, color = BRAND, bg = "rgba(11,42,27,0.07)", size = 38, radius = 10, fontSize = 18 }) => (
  <span style={{ width: size, height: size, borderRadius: radius, background: bg, color, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize, flex: "0 0 auto" }}>
    {children}
  </span>
);

const cardTitle = (icon, text, color = BRAND, bg = "rgba(11,42,27,0.07)") => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 10, fontWeight: 600, color: BRAND }}>
    <Chip color={color} bg={bg} size={28} radius={8} fontSize={14}>{icon}</Chip>
    {text}
  </span>
);

const StatCard = ({ icon, label, value, sub, color, tint }) => (
  <Card size="small" bordered={false} style={cardStyle} bodyStyle={{ padding: 16 }}>
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
      <Chip color={color || BRAND} bg={tint || "rgba(11,42,27,0.07)"}>{icon}</Chip>
      <div style={{ minWidth: 0 }}>
        <div style={{ color: "rgba(0,0,0,0.45)", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: color || BRAND, lineHeight: 1.2, marginTop: 2 }}>{value}</div>
        {sub ? <div style={{ color: "rgba(0,0,0,0.45)", fontSize: 12, marginTop: 1 }}>{sub}</div> : null}
      </div>
    </div>
  </Card>
);

const Line = ({ label, children }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
    <span style={{ color: "rgba(0,0,0,0.55)" }}>{label}</span>
    <span style={{ fontWeight: 600, textAlign: "right" }}>{children}</span>
  </div>
);

/**
 * Overview tab of the asset passport — a decision-ready snapshot assembled from
 * the data we actually hold (FacilityController::actionOverview). Machinery-focused.
 */
const AssetOverview = (props) => {
  const { overview, loading, assetId, onRefresh, modalVisible } = props;
  const o = overview || {};

  const mapRef = useRef(null);

  const lat = o.lat ? Number(o.lat) : null;
  const lng = o.lng ? Number(o.lng) : null;
  const hasLocation = lat !== null && lng !== null && !Number.isNaN(lat) && !Number.isNaN(lng);

  useEffect(() => {
    if (!hasLocation) return;
    const t1 = setTimeout(() => {
      try {
        if (mapRef.current && mapRef.current.leafletElement) {
          mapRef.current.leafletElement.invalidateSize();
        }
      } catch (e) { /* noop */ }
    }, 100);
    const t2 = setTimeout(() => {
      try {
        if (mapRef.current && mapRef.current.leafletElement) {
          mapRef.current.leafletElement.invalidateSize();
        }
      } catch (e) { /* noop */ }
    }, 500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [hasLocation, lat, lng, modalVisible]);

  const sd = serviceDue(o);
  const warranty = warrantyStatus(o.warranty_expiry);
  const meter = (o.current_reading !== null && o.current_reading !== undefined && o.current_reading !== "")
    ? `${o.current_reading} ${o.reading_unit || ""}`.trim()
    : "—";
  const makeModel = [o.make, o.model].filter(Boolean).join(" ") || "—";
  const insp = o.safety && o.safety.last_inspection;
  const openDefects = o.safety ? o.safety.open_defects : 0;
  // Machinery-only sections (meter, service, pre-start, warranty) have no data
  // for other summary-eligible categories like spray, so hide them there.
  const isMachinery = o.category === "machinery";
  const doNotOperate = o.state === "locked_out" || o.state === "out_of_service";

  const moreMenu = (
    <Menu>
      {props.onLogService && <Menu.Item key="svc" icon={<ToolOutlined />} onClick={props.onLogService}>Log Service</Menu.Item>}
      {props.onPrintQr && <Menu.Item key="qr" icon={<QrcodeOutlined />} onClick={props.onPrintQr}>QR Code</Menu.Item>}
    </Menu>
  );

  if (loading) {
    return <div style={{ textAlign: "center", padding: 60 }}><Spin size="large" /></div>;
  }

  return (
    <div style={{ padding: "4px 0" }}>
      {/* ---- Header ---- */}
      <Card bordered={false} style={{ borderRadius: 12, marginBottom: 16, boxShadow: CARD_SHADOW }} bodyStyle={{ padding: 18 }}>
        <Row gutter={[18, 16]} align="middle">
          <Col xs={24} sm={8} md={6}>
            {o.photo ? (
              <img src={o.photo} alt={o.title} style={{ width: "100%", height: 170, objectFit: "cover", borderRadius: 12 }} />
            ) : (
              <div className="asset-image-uploader">
                <FileUploader
                  action={`${apiEndpoint.images}?object_id=${assetId}&type=asset`}
                  type="asset"
                  isCompressFile
                  showUploadList={false}
                  maxCount={1}
                  handleOnSuccess={() => onRefresh && onRefresh()}
                  innerElement={
                    <div style={{ width: "100%", height: 170, borderRadius: 12, border: "2px dashed rgba(11,42,27,0.2)", background: "rgba(11,42,27,0.03)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: BRAND, cursor: "pointer" }}>
                      <CameraOutlined style={{ fontSize: 30 }} />
                      <div style={{ marginTop: 8, fontSize: 13, fontWeight: 500 }}>Upload image</div>
                      <div style={{ fontSize: 11, color: "rgba(0,0,0,0.4)" }}>Click or drop a photo</div>
                    </div>
                  }
                />
              </div>
            )}
          </Col>
          <Col xs={24} sm={16} md={18}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <h2 style={{ margin: 0, color: BRAND, fontWeight: 700 }}>{dash(o.title)}</h2>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "3px 12px", borderRadius: 999, background: doNotOperate ? "rgba(226,78,76,0.12)" : "rgba(85,159,47,0.12)", color: doNotOperate ? RED : GREEN, fontWeight: 700, fontSize: 12, letterSpacing: 0.4 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: stateColor(o.state) }} />
                {stateLabel(o.state).toUpperCase()}
              </span>
            </div>
            <div style={{ color: "rgba(0,0,0,0.55)", marginTop: 6 }}>
              {makeModel !== "—" ? makeModel : dash(o.category)}
              {o.serial_vin ? ` · Serial ${o.serial_vin}` : ""}
            </div>
            <div style={{ color: "rgba(0,0,0,0.5)", marginTop: 8, display: "flex", gap: 18, flexWrap: "wrap", fontSize: 13 }}>
              <span><EnvironmentOutlined /> {dash(o.property_name)}{o.location_name ? ` · ${o.location_name}` : ""}</span>
              <span><DashboardOutlined /> {meter}</span>
              <span><ClockCircleOutlined /> {fromNow(o.last_activity)}</span>
            </div>
            <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
              {props.onStartPreStart && (
                <Button type="primary" icon={<PlayCircleOutlined />} style={{ background: GREEN, borderColor: GREEN }} onClick={props.onStartPreStart}>
                  Start Pre-Start
                </Button>
              )}
              {props.onAssignOperator && (
                <Button icon={<UserOutlined />} onClick={props.onAssignOperator}>
                  {o.operator ? o.operator.name : (isMachinery ? "Assign Operator" : "Assign User")}
                </Button>
              )}
              {props.onUnassignOperator && o.operator && (
                <Popconfirm
                  title={`Remove ${o.operator.name} as ${isMachinery ? "operator" : "user"}?`}
                  onConfirm={props.onUnassignOperator}
                  okText="Remove"
                  cancelText="Cancel"
                  placement="topRight"
                >
                  <Button icon={<UserOutlined />}>
                    {isMachinery ? "Unassign Operator" : "Unassign User"}
                  </Button>
                </Popconfirm>
              )}
              {props.onReportIssue && isMachinery && (
                <Button icon={<WarningOutlined />} onClick={props.onReportIssue}>Report Issue</Button>
              )}
              {(props.onLogService || props.onPrintQr) && (
                <Dropdown overlay={moreMenu} trigger={["click"]}>
                  <Button>More <DownOutlined /></Button>
                </Dropdown>
              )}
            </div>
          </Col>
        </Row>
      </Card>

      {/* ---- Operational status ---- */}
      <Card bordered={false} style={{ borderRadius: 12, marginBottom: 16, boxShadow: CARD_SHADOW }} bodyStyle={{ padding: 18 }}>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "rgba(0,0,0,0.45)", marginBottom: 14, fontWeight: 600 }}>Operational Status</div>
        <Row gutter={[16, 16]}>
          <Col xs={12} md={6}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Chip color={stateColor(o.state)} bg="rgba(0,0,0,0.04)" size={34} fontSize={14}><SafetyOutlined /></Chip>
              <div>
                <b style={{ color: stateColor(o.state) }}>{stateLabel(o.state)}</b>
                <div style={{ color: "rgba(0,0,0,0.45)", fontSize: 12 }}>{o.state === "available" ? "Ready for use" : "Check status"}</div>
              </div>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Chip bg="rgba(0,0,0,0.04)" size={34} fontSize={14}><UserOutlined /></Chip>
              <div>
                <div style={{ color: "rgba(0,0,0,0.45)", fontSize: 12 }}>Operator</div>
                <b>{o.operator ? o.operator.name : "Not assigned"}</b>
              </div>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Chip bg="rgba(0,0,0,0.04)" size={34} fontSize={14}><EnvironmentOutlined /></Chip>
              <div>
                <div style={{ color: "rgba(0,0,0,0.45)", fontSize: 12 }}>Location</div>
                <b>{o.location_name || o.property_name || "—"}</b>
              </div>
            </div>
          </Col>
          {isMachinery && (
          <Col xs={12} md={6}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Chip bg="rgba(0,0,0,0.04)" size={34} fontSize={14}><DashboardOutlined /></Chip>
              <div>
                <div style={{ color: "rgba(0,0,0,0.45)", fontSize: 12 }}>Meter Reading</div>
                <b>{meter}</b>
              </div>
            </div>
          </Col>
          )}
        </Row>
      </Card>

      {/* ---- Stat row (machinery only — service/meter/pre-start) ---- */}
      {isMachinery && (
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <StatCard icon={<ToolOutlined />} label="Service Due" value={sd.main} sub={sd.sub}
            color={sd.overdue ? RED : AMBER} tint={sd.overdue ? "rgba(226,78,76,0.1)" : "rgba(245,160,27,0.12)"} />
        </Col>
        <Col xs={12} md={6}>
          <StatCard icon={<WarningOutlined />} label="Open Items" value={openDefects + " open"}
            sub={o.safety && o.safety.critical_defects ? `${o.safety.critical_defects} critical` : (o.safety && o.safety.open_work_orders ? `${o.safety.open_work_orders} work orders` : "None")}
            color={openDefects ? RED : GREEN} tint={openDefects ? "rgba(226,78,76,0.1)" : "rgba(85,159,47,0.12)"} />
        </Col>
        <Col xs={12} md={6}>
          <StatCard icon={<DashboardOutlined />} label="Meter Reading" value={meter} sub={o.reading_unit || ""}
            color={BRAND} tint="rgba(11,42,27,0.07)" />
        </Col>
        <Col xs={12} md={6}>
          <StatCard icon={<SafetyOutlined />} label="Last Pre-Start"
            value={insp ? (insp.status === "pass" ? "Passed" : insp.status === "fail" ? "Failed" : dash(insp.status)) : "—"}
            sub={insp ? fmtDate(insp.date) : "No inspections"}
            color={insp && insp.status === "fail" ? RED : GREEN}
            tint={insp && insp.status === "fail" ? "rgba(226,78,76,0.1)" : "rgba(85,159,47,0.12)"} />
        </Col>
      </Row>
      )}

      {/* ---- Safety & Maintenance (machinery only) ---- */}
      {isMachinery && (
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <Card size="small" bordered={false} title={cardTitle(<SafetyOutlined />, "Safety & Compliance")} style={cardStyle} headStyle={{ border: "none" }}>
            <Line label="Pre-start">
              {insp ? (
                insp.status === "pass"
                  ? <span style={{ color: GREEN }}><CheckCircleFilled /> Passed</span>
                  : insp.status === "fail"
                    ? <span style={{ color: RED }}><CloseCircleFilled /> Failed</span>
                    : dash(insp.status)
              ) : "—"}
            </Line>
            <Line label="Warranty"><Tag color={warranty.color}>{warranty.label}</Tag></Line>
            <Line label="Open defects">
              {openDefects ? <span style={{ color: RED }}><WarningOutlined /> {openDefects}</span> : <span style={{ color: GREEN }}><CheckCircleFilled /> None</span>}
            </Line>
            <Line label="Registration">{dash(o.registration)}</Line>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card size="small" bordered={false} title={cardTitle(<ToolOutlined />, "Maintenance", AMBER, "rgba(245,160,27,0.14)")} style={cardStyle} headStyle={{ border: "none" }}>
            <Line label="Next service">{o.next_service_due_date ? fmtDate(o.next_service_due_date) : (o.next_service_due_reading ? `${o.next_service_due_reading} ${o.reading_unit || ""}`.trim() : "—")}</Line>
            <Line label="Due in"><span style={{ color: sd.overdue ? RED : undefined }}>{sd.main}</span></Line>
            <Line label="Last service">{o.service && o.service.last ? fmtDate(o.service.last.date) : "—"}</Line>
            <Line label="Service interval">{o.service_interval ? `${o.service_interval} ${o.service_interval_unit || ""}`.trim() : "—"}</Line>
          </Card>
        </Col>
      </Row>
      )}

      {/* ---- Location · Documents (Forms & Sensors live under Asset Summary) ---- */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card size="small" bordered={false} title={cardTitle(<EnvironmentOutlined />, "Location")} style={cardStyle} headStyle={{ border: "none" }} bodyStyle={{ padding: 12 }}>
            {hasLocation ? (
              <LeafletMap
                ref={mapRef}
                center={[lat, lng]}
                zoom={16}
                style={{ height: 180, borderRadius: 8 }}
                zoomControl={false}
                attributionControl={false}
                dragging={false}
                scrollWheelZoom={false}
                doubleClickZoom={false}
                keyboard={false}
                boxZoom={false}
                tap={false}
              >
                <TileLayer url={SAT_TILES} />
                <CircleMarker center={[lat, lng]} radius={8} color="#fff" weight={2} fillColor={RED} fillOpacity={1} />
              </LeafletMap>
            ) : (
              <div style={{ height: 180, borderRadius: 8, background: "rgba(0,0,0,0.04)", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(0,0,0,0.4)" }}>
                No location set
              </div>
            )}
            <div style={{ marginTop: 8, color: "rgba(0,0,0,0.55)", fontSize: 13 }}>
              {o.location_name || o.property_name || (hasLocation ? `${lat.toFixed(5)}, ${lng.toFixed(5)}` : "—")}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card
            size="small"
            bordered={false}
            title={cardTitle(<PaperClipOutlined />, "Documents")}
            style={cardStyle}
            headStyle={{ border: "none" }}
            extra={
              <FileUploader
                action={apiEndpoint.attachments}
                name="Attachment[file]"
                type="document"
                data={{ object_type: "facility", object_id: assetId }}
                showUploadList={false}
                maxCount={1}
                handleOnSuccess={() => onRefresh && onRefresh()}
                innerElement={<a style={{ fontSize: 13, fontWeight: 500 }}>+ Upload</a>}
              />
            }
          >
            {!o.attachments || o.attachments.length === 0 ? (
              <Empty description="No documents" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              o.attachments.map((a, i) => (
                <Line key={i} label={<a href={a.full_path || a.url} target="_blank" rel="noopener noreferrer">{a.display_name || a.name || `Document ${i + 1}`}</a>}>
                  <DownloadOutlined style={{ color: "rgba(0,0,0,0.4)" }} />
                </Line>
              ))
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AssetOverview;
